// Single source of truth for every URL in the web app. Never write a path string in a component:
// import { paths } from '../routes/paths' and use paths.login, paths.event(id), paths.admin.users…
// so a URL can change in one place. Path VALUES are kept stable on purpose (emails and the
// backend link to /reset-password, /organiser/reset-password, …).

const ADMIN = '/admin';
const ORGANISER = '/organizer-dashboard';

export const paths = {
  home: '/',
  // Staff mode (door check-in): every screen has its own URL, so refresh, deep links and the back button all work.
  staff: {
    root: '/staff', // goes to the first event you can check in
    event: (id = ':eventId') => `/staff/${id}`, // home: door counts
    scan: (id = ':eventId') => `/staff/${id}/scan`,
    scanResult: (id = ':eventId') => `/staff/${id}/scan/result`, // the answer to a scan (needs the result it was opened with)
    manual: (id = ':eventId') => `/staff/${id}/manual`, // ?q= is the search
    attendee: (id = ':eventId', orderId = ':orderId') => `/staff/${id}/manual/${orderId}`,
  },
  event: (id = ':id') => `/event/${id}`, // paths.event() is the route pattern, paths.event(5) a real link
  // checkout steps: tickets -> checkout -> confirmed
  eventTickets: (id = ':id') => `/event/${id}/tickets`,
  eventCheckout: (id = ':id') => `/event/${id}/checkout`,
  eventConfirmed: (id = ':id') => `/event/${id}/confirmed`,
  eventTicket: (id = ':id') => `/event/${id}/ticket`,
  // the buyer's own area
  myTickets: '/tickets',
  nearby: '/nearby', // the map and the nearest events (?lat&lng&label&radiusKm)
  notifications: '/notifications',
  notificationPreferences: '/notifications/preferences',
  orders: '/orders',
  order: (id = ':id') => `/orders/${id}`,
  orderTickets: (id = ':id') => `/orders/${id}/tickets`,
  orderRefund: (id = ':id') => `/orders/${id}/refund`, // "Request a refund" (Figma)
  orderRefundStatus: (id = ':id', refundId = ':refundId') => `/orders/${id}/refund/${refundId}`, // the request and how it stands

  // Attendee auth
  login: '/login',
  register: '/register',
  registerVerify: '/register/verify',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  // CMS pages written in Admin > Pages. The sign-up screen links the two the site itself needs (their addresses are locked).
  page: (slug = ':slug') => `/page/${slug}`,
  terms: '/page/terms-and-conditions',
  privacy: '/page/privacy-policy',
  dataRights: '/data-rights', // Privacy & data rights (signed in)
  paymentMethods: '/payment-methods', // saved cards (signed in)
  attendeeDashboard: '/dashboard',

  // Organiser
  organiser: {
    login: '/organiser/login',
    apply: '/organiser/apply',
    forgotPassword: '/organiser/forgot-password',
    resetPassword: '/organiser/reset-password',
    root: ORGANISER,
    dashboard: ORGANISER,
    events: `${ORGANISER}/events`,
    eventNew: `${ORGANISER}/events/new`,
    // The create-event steps. paths.organiser.eventEdit() is the route pattern, eventEdit(5) a real link.
    eventEdit: (id = ':id') => `${ORGANISER}/events/${id}`,
    eventTickets: (id = ':id') => `${ORGANISER}/events/${id}/tickets`,
    eventReview: (id = ':id') => `${ORGANISER}/events/${id}/review`,
    ticketTypes: `${ORGANISER}/ticket-types`,
    // the add/edit page: ticketTypeNew(5) is a real link, ticketTypeNew() the route pattern
    ticketTypeNew: (eventId = ':eventId') => `${ORGANISER}/ticket-types/${eventId}/new`,
    ticketTypeEdit: (eventId = ':eventId', id = ':id') => `${ORGANISER}/ticket-types/${eventId}/${id}`,
    payouts: `${ORGANISER}/payouts`,
    payoutOrders: `${ORGANISER}/payouts/orders`,
    reports: `${ORGANISER}/reports`,
    broadcast: `${ORGANISER}/broadcast`,
    staff: `${ORGANISER}/staff`,
    companyProfile: `${ORGANISER}/company-profile`,
    verification: `${ORGANISER}/company-profile/verification`,
    verificationUpdate: `${ORGANISER}/company-profile/verification/update`,
    stripe: `${ORGANISER}/company-profile/stripe`,
    profile: `${ORGANISER}/profile`,
  },

  // Admin: /admin redirects to the first section; every section has its own URL.
  admin: {
    root: ADMIN,
    organisers: `${ADMIN}/organisers`,
    users: `${ADMIN}/users`,
    events: `${ADMIN}/events`,
    categories: `${ADMIN}/categories`,
    staff: `${ADMIN}/staff`,
    orders: `${ADMIN}/orders`,
    refunds: `${ADMIN}/refunds`,
    reports: `${ADMIN}/reports`,
    pages: `${ADMIN}/pages`,
    pageNew: `${ADMIN}/pages/new`,
    pageEdit: (id = ':id') => `${ADMIN}/pages/${id}`,
    settings: `${ADMIN}/settings`,
    settingsPayments: `${ADMIN}/settings/payments`,
    settingsSms: `${ADMIN}/settings/sms`,
    settingsEmail: `${ADMIN}/settings/email`,
    settingsSocial: `${ADMIN}/settings/social`,
    settingsMaps: `${ADMIN}/settings/maps`,
    settingsPush: `${ADMIN}/settings/push`,
    auditLog: `${ADMIN}/audit-log`,
    profile: `${ADMIN}/profile`,
  },
};
